import { IconButton } from "@/shared/ui/primitives";
import { CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

type Toast = {
  id: number;
  tone: "success" | "error" | "info";
  title: string;
  description?: string;
  /** One follow-up, e.g. "Annulla": the toast then stays longer. */
  action?: { label: string; onClick: () => void };
};
type ToastInput = Omit<Toast, "id">;
type ToastContextValue = { pushToast: (toast: ToastInput) => void };

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback(
    (id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)),
    [],
  );
  const pushToast = useCallback(
    (toast: ToastInput) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { ...toast, id }]);
      window.setTimeout(() => dismiss(id), toast.action ? 9_000 : 5_500);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ pushToast }), [pushToast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-label="Notifiche">
        {toasts.map((toast) => {
          const Icon =
            toast.tone === "success" ? CheckCircle2 : toast.tone === "error" ? TriangleAlert : Info;
          return (
            <div className={`toast toast--${toast.tone}`} key={toast.id}>
              <Icon aria-hidden="true" size={19} />
              <div>
                <strong>{toast.title}</strong>
                {toast.description ? <p>{toast.description}</p> : null}
              </div>
              {toast.action ? (
                <button
                  type="button"
                  className="text-button toast__action"
                  onClick={() => {
                    dismiss(toast.id);
                    toast.action?.onClick();
                  }}
                >
                  {toast.action.label}
                </button>
              ) : null}
              <IconButton
                type="button"
                aria-label="Chiudi notifica"
                onClick={() => dismiss(toast.id)}
              >
                <X aria-hidden="true" size={17} />
              </IconButton>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast deve essere usato dentro ToastProvider");
  return value;
}
